/**
 * Transaction utility functions — pure helpers with no React dependency.
 *
 * Extracted from TransactionsListPage.js for reuse by TransactionDrawer
 * and other transaction-related components.
 */

/**
 * Sub-cent display threshold (~0.9 cents).
 * Used to suppress floating-point noise when displaying USD amounts.
 * Values below this threshold are treated as effectively zero for display.
 *
 * Also used as comparison epsilon for client-side refund cap validation
 * (backend enforces limits independently via PaymentSessionService).
 */
export const AMOUNT_DISPLAY_THRESHOLD = 0.009;

/**
 * Check whether a USD amount is effectively zero (below display threshold).
 */
export const isEffectivelyZero = (amount) =>
  !amount || Math.abs(amount) < AMOUNT_DISPLAY_THRESHOLD;

/**
 * Parse a value to float, returning null if not a finite number.
 */
export const parseOptionalFloat = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Get the raw settlement USD (received_usd) from a transaction.
 */
export const getRawSettlementUsd = (transaction) =>
  parseOptionalFloat(transaction?.received_usd);

/**
 * Get the merchant-facing settlement USD (merchant_received_usd).
 */
export const getSettlementUsd = (transaction) =>
  parseOptionalFloat(transaction?.merchant_received_usd);

/**
 * Determine settlement status from transaction data.
 */
export const getSettlementStatus = (transaction) => {
  if (transaction?.settlement_status) {
    return transaction.settlement_status;
  }
  return getSettlementUsd(transaction) !== null ? 'settled' : null;
};

/**
 * Get the merchant settled USD amount.
 */
export const getMerchantSettledUsd = (transaction) => {
  const direct = parseOptionalFloat(transaction?.merchant_settled_usd);
  if (direct !== null) return direct;
  const settlementUsd = getSettlementUsd(transaction);
  if (settlementUsd === null) return null;
  return settlementUsd;
};

/**
 * Get the merchant shortfall USD amount.
 */
export const getMerchantShortfallUsd = (transaction) => {
  const direct = parseOptionalFloat(transaction?.merchant_shortfall_usd);
  if (direct !== null) return direct;
  const settlementUsd = getSettlementUsd(transaction);
  if (settlementUsd === null) return null;
  const expectedAmount = parseOptionalFloat(transaction?.expected_payment_usd)
    ?? parseOptionalFloat(transaction?.order_amount_usd)
    ?? 0;
  const shortfall = expectedAmount - settlementUsd;
  return shortfall > AMOUNT_DISPLAY_THRESHOLD ? shortfall : 0;
};

/**
 * Get the crypto overpayment USD amount.
 */
export const getCryptoOverpaymentUsd = (transaction) => {
  const direct = parseOptionalFloat(transaction?.crypto_overpayment_usd);
  if (direct !== null) return direct;
  const discrepancy = parseOptionalFloat(transaction?.discrepancy_usd);
  return discrepancy > 0 ? discrepancy : 0;
};

/**
 * Get the crypto shortfall USD amount.
 */
export const getCryptoShortfallUsd = (transaction) => {
  const direct = parseOptionalFloat(transaction?.crypto_shortfall_usd);
  if (direct !== null) return direct;
  const discrepancy = parseOptionalFloat(transaction?.discrepancy_usd);
  return discrepancy < 0 ? Math.abs(discrepancy) : 0;
};

/**
 * Get the crypto mismatch USD amount (positive=overpayment, negative=shortfall).
 */
export const getCryptoMismatchUsd = (transaction) => {
  const overpayment = getCryptoOverpaymentUsd(transaction);
  if (overpayment > 0) return overpayment;
  const shortfall = getCryptoShortfallUsd(transaction);
  if (shortfall > 0) return -shortfall;
  const discrepancy = parseOptionalFloat(transaction?.discrepancy_usd);
  return discrepancy !== null ? discrepancy : null;
};

/**
 * Calculate net payout — prefers backend fields (authoritative).
 */
export const getNetPayout = (tx) => {
  if (!tx) return null;

  // Denied payments never produce a merchant payout
  if (tx?.resolution_action === 'deny') return 0;

  let netPayoutBase = null;

  // For needs_resolution, use backend-projected value
  if (tx?.status === 'needs_resolution') {
    const projectedNetPayout = parseOptionalFloat(tx?.projected_net_payout_usd);
    if (projectedNetPayout !== null) {
      netPayoutBase = projectedNetPayout;
    }
  }

  // Authoritative net payout from POS
  if (netPayoutBase === null) {
    const backendNetPayout = parseOptionalFloat(tx?.net_payout_usd);
    if (backendNetPayout !== null) {
      netPayoutBase = backendNetPayout;
    }
  }

  // Legacy fallback: accepted amount minus total fees (all backend fields)
  if (netPayoutBase === null) {
    const acceptedAmount = parseOptionalFloat(tx?.accepted_amount_usd);
    const totalFees = parseOptionalFloat(tx?.actual_total_fees_usd);
    if (acceptedAmount !== null && totalFees !== null) {
      netPayoutBase = acceptedAmount - totalFees;
    }
  }

  // Denied/refunded with missing data defaults to $0
  if (netPayoutBase === null && tx?.status === 'refunded') return 0;
  if (netPayoutBase === null) return null;

  const refundedFromPayout = parseOptionalFloat(tx?.refunded_from_payout_usd) ?? 0;
  let netAfterRefunds = netPayoutBase - refundedFromPayout;

  const merchantFee = parseOptionalFloat(tx?.actual_merchant_fee_usd) ?? 0;
  const isPayoutFullyRefunded = refundedFromPayout > AMOUNT_DISPLAY_THRESHOLD
    && netPayoutBase > AMOUNT_DISPLAY_THRESHOLD
    && refundedFromPayout >= (netPayoutBase - 0.01);

  // Match Woo ledger behavior: if payout is fully refunded, merchant fee remains a net loss.
  if (isPayoutFullyRefunded && merchantFee > AMOUNT_DISPLAY_THRESHOLD) {
    netAfterRefunds = -merchantFee;
  }

  return netAfterRefunds;
};

/**
 * Get CSS class for signed amount display.
 */
export const getSignedAmountClass = (value, positiveClass = 'text-success') => {
  const parsed = parseOptionalFloat(value);
  if (parsed === null) return 'text-muted';
  if (parsed < -AMOUNT_DISPLAY_THRESHOLD) return 'text-danger';
  if (parsed > AMOUNT_DISPLAY_THRESHOLD) return positiveClass;
  return 'text-muted';
};

/**
 * Fee display logic based on fee_payer mode.
 */
export const getFeeSummary = (tx) => {
  const feePayer = tx?.fee_payer || 'merchant';
  const merchantFee = parseOptionalFloat(tx?.actual_merchant_fee_usd) ?? 0;
  const customerFee = parseOptionalFloat(tx?.actual_customer_fee_usd) ?? 0;
  const totalFees = parseOptionalFloat(tx?.actual_total_fees_usd) ?? (merchantFee + customerFee);

  // For denied payments, backend returns hard costs as total fees
  const isHardCostsOnly = tx?.resolution_action === 'deny';
  const hardCosts = isHardCostsOnly ? totalFees : 0;

  // Get fee percentages for display
  const merchantFeePct = parseOptionalFloat(tx?.merchant_fee_pct) ?? 0;
  const merchantFeeFlat = parseOptionalFloat(tx?.merchant_fee_flat_usd) ?? 0;
  const customerFeePct = parseOptionalFloat(tx?.customer_fee_pct) ?? 0;
  const customerFeeFlat = parseOptionalFloat(tx?.customer_fee_flat_usd) ?? 0;

  // Build fee label with percentage if available
  const buildFeeLabel = (baseName, pct, flat) => {
    const parts = [];
    if (pct > 0) parts.push(`${pct}%`);
    if (flat > 0) parts.push(`$${flat.toFixed(2)}`);
    if (parts.length > 0) {
      return `${baseName} (${parts.join(' + ')})`;
    }
    return baseName;
  };

  switch (feePayer) {
    case 'merchant':
      return {
        feePayer,
        showMerchantFee: true,
        merchantFeeLabel: buildFeeLabel('Processing Fee', merchantFeePct, merchantFeeFlat),
        merchantFee,
        showCustomerFee: false,
        customerFeeLabel: null,
        customerFee: 0,
        customerPaidNote: 'no additional fees',
        showTotalFees: false,
        totalFees,
        hardCosts,
        isHardCostsOnly
      };
    case 'customer':
      return {
        feePayer,
        showMerchantFee: false,
        merchantFeeLabel: null,
        merchantFee: 0,
        showCustomerFee: true,
        customerFeeLabel: buildFeeLabel("Customer's Fee", customerFeePct, customerFeeFlat),
        customerFee,
        customerPaidNote: null,
        showTotalFees: false,
        totalFees,
        hardCosts,
        isHardCostsOnly
      };
    case 'shared':
      return {
        feePayer,
        showMerchantFee: true,
        merchantFeeLabel: buildFeeLabel('Your Share', merchantFeePct, merchantFeeFlat),
        merchantFee,
        showCustomerFee: true,
        customerFeeLabel: buildFeeLabel("Customer's Share", customerFeePct, customerFeeFlat),
        customerFee,
        customerPaidNote: null,
        showTotalFees: true,
        totalFees,
        hardCosts,
        isHardCostsOnly
      };
    default:
      return {
        feePayer: 'merchant',
        showMerchantFee: true,
        merchantFeeLabel: buildFeeLabel('Processing Fee', merchantFeePct, merchantFeeFlat),
        merchantFee,
        showCustomerFee: false,
        customerFeeLabel: null,
        customerFee: 0,
        customerPaidNote: null,
        showTotalFees: false,
        totalFees,
        hardCosts,
        isHardCostsOnly
      };
  }
};

/**
 * Check for settlement shortfall.
 */
export const hasSettlementShortfall = (tx) => {
  const settlementStatus = getSettlementStatus(tx);
  if (settlementStatus !== 'settled') return false;
  const settlement = getSettlementUsd(tx) ?? 0;
  const orderAmount = parseOptionalFloat(tx?.order_amount_usd) ?? 0;
  return settlement < orderAmount - 0.01; // 1 cent tolerance
};
